import {PriorityQueue} from "@cm/utils"

type RequestId = number

type PendingRequest<RequestType, TResponseMap extends Record<string, unknown>> = {
    request: RequestType
    responseKind: keyof TResponseMap
    abortSignal: AbortSignal
    resolve: (result: TResponseMap[keyof TResponseMap]) => void
    reject: (error: unknown) => void
}

export abstract class AbstractTileLoader<RequestType, TResponseMap extends Record<string, unknown>> {
    protected abstract startRequest(requestId: number, request: RequestType, responseKind: keyof TResponseMap, abortSignal: AbortSignal): void

    dispose(): void {}

    async requestTile<K extends keyof TResponseMap>(
        request: RequestType,
        responseKind: K,
        priority: number,
        abortSignal: AbortSignal,
    ): Promise<TResponseMap[K]> {
        return new Promise<TResponseMap[K]>((resolve, reject) => {
            const requestId = this.nextRequestId++
            this.pendingById.set(requestId, {
                request,
                responseKind,
                abortSignal,
                // resolve narrows K -> TResponseMap[K] at the call site above; pendingById stores
                // requests of possibly-different K side by side, so it's widened to the union here.
                // Sound because resolveRequest is only ever invoked (by this request's own
                // startRequest implementation) with a result matching this same request's
                // responseKind - TypeScript can't verify that correlation across a Record-keyed
                // map itself, hence the cast.
                resolve: resolve as (result: TResponseMap[keyof TResponseMap]) => void,
                reject,
            })
            this.queue.enqueue(requestId, priority)
            abortSignal.addEventListener("abort", () => this.cancel(requestId), {once: true})
            this.drain()
        })
    }

    get queueLength(): number {
        return this.queue.size
    }

    protected resolveRequest(requestId: RequestId, result: TResponseMap[keyof TResponseMap]): void {
        const request = this.pendingById.get(requestId)
        if (!request) {
            return
        }
        this.pendingById.delete(requestId)
        this.active--
        request.resolve(result)
        this.drain()
    }

    protected rejectRequest(requestId: RequestId, error: unknown): void {
        const request = this.pendingById.get(requestId)
        if (!request) {
            return
        }
        this.pendingById.delete(requestId)
        this.active--
        request.reject(error)
        this.drain()
    }

    private drain(): void {
        while (this.active < this.maxConcurrent) {
            const requestId = this.queue.dequeue()
            if (requestId === null) {
                break
            }
            const request = this.pendingById.get(requestId)
            if (!request) {
                throw new Error(`AbstractTileLoader: request ${requestId} missing`)
            }
            this.active++
            this.startRequest(requestId, request.request, request.responseKind, request.abortSignal)
        }
    }

    private cancel(requestId: RequestId): void {
        const request = this.pendingById.get(requestId)
        if (!request) {
            return
        }
        this.pendingById.delete(requestId)
        if (this.queue.contains(requestId)) {
            this.queue.remove(requestId)
        } else {
            this.active--
        }
        request.reject(new DOMException("AbortError", "AbortError"))
        this.drain()
    }

    private readonly pendingById = new Map<RequestId, PendingRequest<RequestType, TResponseMap>>()
    private readonly queue = new PriorityQueue<RequestId>()
    private nextRequestId = 1
    private maxConcurrent = 64
    private active = 0
}
